import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { XIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { POPOVER_SURFACE_CLASSES } from '@/components/ui/surface';
import { useModalPortalContainer } from '@/components/Modal/modalPortalContainer';

const PopoverRoot = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverClose = PopoverPrimitive.Close;

interface PopoverProps extends React.ComponentPropsWithoutRef<'div'> {
    title?: string;
    subtitle?: string;
    headerActions?: React.ReactNode;
    onClose?: () => void;
}

function Popover({
    title,
    subtitle,
    headerActions,
    onClose,
    className,
    children,
    ...rest
}: PopoverProps) {
    const { t } = useTranslation();
    const hasHeader = Boolean(title || subtitle || headerActions || onClose);

    return (
        <div
            className={cn(
                POPOVER_SURFACE_CLASSES,
                'flex flex-col gap-3 p-4',
                className,
            )}
            {...rest}
        >
            {hasHeader && (
                <div className="flex items-center gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {title && (
                            <span className="text-sm-medium text-white-100">
                                {title}
                            </span>
                        )}
                        {subtitle && (
                            <span className="text-xs-medium text-grey-200">
                                {subtitle}
                            </span>
                        )}
                    </div>
                    {(headerActions || onClose) && (
                        <div className="flex shrink-0 items-center gap-4">
                            {headerActions}
                            {onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t('button.close')}
                                    className="text-grey-200 transition-colors hover:text-white-100"
                                >
                                    <XIcon className="size-5" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
            {children}
        </div>
    );
}

interface PopoverPositionerProps extends React.ComponentPropsWithoutRef<
    typeof PopoverPrimitive.Content
> {
    container?: React.ComponentPropsWithoutRef<
        typeof PopoverPrimitive.Portal
    >['container'];
}

const PopoverPositioner = React.forwardRef<
    React.ComponentRef<typeof PopoverPrimitive.Content>,
    PopoverPositionerProps
>(
    (
        { className, align = 'center', sideOffset = 4, container, ...props },
        ref,
    ) => {
        const modalPortalContainer = useModalPortalContainer();

        return (
            <PopoverPrimitive.Portal
                container={container ?? modalPortalContainer}
            >
                <PopoverPrimitive.Content
                    ref={ref}
                    align={align}
                    sideOffset={sideOffset}
                    className={cn(
                        'z-50 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto scrollbar-grey-600 outline-none origin-[var(--radix-popover-content-transform-origin)] ease-out-expo data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                        className,
                    )}
                    {...props}
                />
            </PopoverPrimitive.Portal>
        );
    },
);
PopoverPositioner.displayName = 'PopoverPositioner';

interface PopoverContentProps extends PopoverPositionerProps {
    title?: string;
    subtitle?: string;
    onClose?: () => void;
}

const PopoverContent = React.forwardRef<
    React.ComponentRef<typeof PopoverPrimitive.Content>,
    PopoverContentProps
>(({ className, title, subtitle, onClose, children, ...props }, ref) => (
    <PopoverPositioner ref={ref} {...props}>
        <Popover
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            className={className}
        >
            {children}
        </Popover>
    </PopoverPositioner>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export {
    Popover,
    PopoverRoot,
    PopoverTrigger,
    PopoverAnchor,
    PopoverClose,
    PopoverPositioner,
    PopoverContent,
};
